import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithPopup,
  deleteUser,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
  EmailAuthProvider,
} from 'firebase/auth';
import { collection, getDocs, writeBatch } from 'firebase/firestore';
import { auth, db, googleProvider, appleProvider } from '../firebase';
import { deleteAccountData } from '../api/viewer';

const AuthContext = createContext();

// Guest user object used when no Firebase user is signed in.
// This allows Apple reviewers (and all users) to use the app immediately.
const GUEST_USER = {
  uid: 'guest',
  email: 'guest@oplixi.com',
  displayName: 'Guest',
  metadata: { creationTime: new Date().toISOString() },
  isGuest: true,
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [authError, setAuthError] = useState(null);
  function signup(email, password) {
    if (!auth) throw new Error('Firebase is not initialized');
    return createUserWithEmailAndPassword(auth, email, password);
  }

  function login(email, password) {
    if (!auth) throw new Error('Firebase is not initialized');
    console.info('[Auth] signInWithEmailAndPassword started');
    return signInWithEmailAndPassword(auth, email, password);
  }

  function logout() {
    if (!auth) throw new Error('Firebase is not initialized');
    return signOut(auth);
  }

  function resetPassword(email) {
    if (!auth) throw new Error('Firebase is not initialized');
    return sendPasswordResetEmail(auth, email);
  }

  function loginWithGoogle() {
    if (!auth) throw new Error('Firebase is not initialized');
    console.info('[Auth] signInWithPopup(google) started');
    return signInWithPopup(auth, googleProvider);
  }

  function loginWithApple() {
    if (!auth) throw new Error('Firebase is not initialized');
    console.info('[Auth] signInWithPopup(apple) started');
    return signInWithPopup(auth, appleProvider);
  }

  // Re-establish a recent credential so a sensitive op (delete) is allowed.
  async function reauthenticate(user, password) {
    const providerId = user.providerData?.[0]?.providerId;
    if (providerId === 'password') {
      if (!password) {
        const err = new Error('Password required to delete this account.');
        err.code = 'auth/password-required';
        throw err;
      }
      const credential = EmailAuthProvider.credential(user.email, password);
      await reauthenticateWithCredential(user, credential);
    } else if (providerId === 'google.com') {
      await reauthenticateWithPopup(user, googleProvider);
    } else if (providerId === 'apple.com') {
      await reauthenticateWithPopup(user, appleProvider);
    } else {
      // Fall back to a popup with whatever provider Firebase reports.
      await reauthenticateWithPopup(user, googleProvider);
    }
  }

  // Purge the Firestore data the client owns directly (My List), so account
  // deletion is complete even if the server endpoint misses it. The client
  // writes these docs itself (users/{uid}/myList), so it can delete them with
  // the same credentials — this closes the path mismatch where the server
  // purge looked at `my_list` while the app writes to `myList`.
  async function purgeClientFirestoreData(uid) {
    if (!db) return;
    // Collections the app writes directly under users/{uid} (My List sync and
    // push-notification device tokens). Purge each so deletion is complete.
    for (const name of ['myList', 'pushTokens']) {
      try {
        const snap = await getDocs(collection(db, 'users', uid, name));
        if (snap.empty) continue;
        const batch = writeBatch(db);
        snap.docs.forEach((d) => batch.delete(d.ref));
        await batch.commit();
      } catch (err) {
        console.warn(`[Auth] Client-side purge of ${name} failed (continuing):`, err.message);
      }
    }
  }

  // Permanently delete the account: purge server-side data, then remove the
  // Firebase Auth user. Firebase requires a recent login for deletion, so we
  // re-authenticate and retry once if the session is stale.
  async function deleteAccount(password) {
    if (!auth) throw new Error('Firebase is not initialized');
    const user = auth.currentUser;
    if (!user || user.isGuest) {
      throw new Error('No signed-in account to delete.');
    }

    // Best-effort server-side purge (watch history etc.) while the ID token is
    // still valid, then a client-side purge of data the app owns directly.
    // Both run before deleteUser — once the Auth user is gone, Firestore rules
    // deny further writes.
    try {
      await deleteAccountData();
    } catch (err) {
      console.warn('[Auth] Server-side data purge failed (continuing):', err.message);
    }
    await purgeClientFirestoreData(user.uid);

    try {
      await deleteUser(user);
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        await reauthenticate(user, password);
        // Re-run the client purge in case reauth refreshed the session after
        // the first attempt was denied; getDocs on an empty collection is cheap.
        await purgeClientFirestoreData(user.uid);
        await deleteUser(user);
      } else {
        throw err;
      }
    }
  }

  useEffect(() => {
    if (!auth) {
      // Firebase not available — use guest user so app still works
      console.warn('[Auth] Firebase auth is null — using guest mode');
      setCurrentUser(GUEST_USER);
      setAuthReady(true);
      setAuthError(null);
      return;
    }

    console.info('[Auth] Setting up onAuthStateChanged listener');
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      console.info('[Auth] onAuthStateChanged fired, user:', user ? user.email : 'guest');
      // Use the real user if signed in, otherwise fall back to guest
      setCurrentUser(user || GUEST_USER);
      setAuthReady(true);
      setAuthError(null);
    });

    return unsubscribe;
  }, []);

  // Always subscribed
  const isSubscribed = true;

  const value = {
    currentUser,
    authReady,
    authError,
    signup,
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    resetPassword,
    deleteAccount,
    isSubscribed,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
